// @flow strict
const preloadImagesList = [];
export function preloadImage(src: string) {
  const img = new Image();
  img.onload = function() {
    const index = preloadImagesList.indexOf(this);
    if (index !== -1) {
      // remove image from the array once it's loaded
      // for memory consumption reasons
      preloadImagesList.splice(index, 1);
    }
  };
  preloadImagesList.push(img);
  img.src = src;
}

export function imgSrc(src: string, noPreload?: "noPreload"): string {
  const url = `${process.env.PUBLIC_URL}${src}`;
  if (noPreload !== "noPreload") {
    preloadImage(url);
  }
  return url;
}

export function path(url: string): string {
  const BASE_PATH =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_DEV_BASE_URL
      : process.env.REACT_APP_BASE_URL;
  return `${BASE_PATH}${url}`;
}

export function asMap<K, V>(entries: Array<[K, V]>): Map<K, V> {
  return new Map(entries);
}

export function toHash<K, V>(entries: Array<V>, hash: (V) => K): { [K]: V } {
  const result: { [K]: V } = {};
  entries.forEach((entry) => {
    result[hash(entry)] = entry;
  });
  return result;
}

export function emptyObject<T>(): $Diff<T, T> {
  return {};
}

export type UnixTime = { seconds: number, nanoseconds: number };
export const DUMMY_TIME: UnixTime = { seconds: -1, nanoseconds: -1 };

export function now(): UnixTime {
  const n = new Date().getTime();
  return { seconds: Math.floor(n / 1000), nanoseconds: 0 };
}

export function secondsToTarget(
  initial: UnixTime,
  seconds: number,
  from?: UnixTime
): number {
  return seconds - (Math.floor((from || now()).seconds - initial.seconds) + 1);
}

export function splitTime(
  seconds: number
): { seconds: number, minutes: number } {
  const sToTarget = Math.floor(seconds / 60);
  const mToTarget = seconds % 60;
  return { seconds: sToTarget, minutes: mToTarget };
}
export function formatDateDistance(initial: UnixTime, target: number): string {
  const totalSecondsToTarget = secondsToTarget(initial, target);
  if (totalSecondsToTarget < 0) {
    return "-";
  }
  const { seconds, minutes } = splitTime(totalSecondsToTarget);
  return `${padTwo(seconds)}:${padTwo(minutes)}`;
}
export function padTwo(n: number): string {
  return ("0" + n).slice(-2);
}

export function formatDistance(
  start: UnixTime,
  end: UnixTime,
  target: number
): string {
  const totalSecondsToTarget = secondsToTarget(start, target, end);
  const { seconds, minutes } = splitTime(totalSecondsToTarget);
  return `${padTwo(seconds)}:${padTwo(minutes)}`;
}

export function typedKeys<T: string>(
  object: $ReadOnly<{ [T]: mixed, ... }>
): Array<T> {
  return Object.keys(object);
}

// Return the values of an object.  Similar to Object.values but flow friendly
export function typedValues<T>(obj: $ReadOnly<{ [mixed]: T }>): Array<T> {
  return Object.keys(obj).map((key) => obj[key]);
}

export function typedEntries<K, T>(
  obj: $ReadOnly<{ [K]: T, ... }>
): Array<[K, T]> {
  return Object.keys(obj).map((key) => [key, obj[key]]);
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(() => resolve(), ms));
}

export function sequentialAll<T>(
  promisesFn: $ReadOnlyArray<(idx: number) => Promise<T>>
): Promise<Array<$NonMaybeType<T>>> {
  return promisesFn.reduce((promiseAcc, promiseFn, idx) => {
    return promiseAcc.then((results) => {
      return promiseFn(idx).then((nextResult) => {
        if (nextResult != null) {
          results.push(nextResult);
        }
        return results;
      });
    });
  }, Promise.resolve([]));
}

// This is a way to do {...original, ...newValues} without flow complaining
export function copyWithValues<T: { ... }>(
  original: $ReadOnly<T>,
  newValues: $Shape<T>
): T {
  //$FlowExpectedError[incompatible-return]
  return Object.assign({}, original, newValues);
}
